import type { MouseEvent } from "react";
import type { StepId, StepStatus } from "./intake-options";

export type ProgressStep = { id: StepId; label: string; anchor: string; status: StepStatus };

function CheckIcon() {
  return <svg aria-hidden="true" viewBox="0 0 16 16" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.2"><path strokeLinecap="round" strokeLinejoin="round" d="m3 8.5 3 3 7-7" /></svg>;
}

const MARKER: Record<StepStatus, string> = {
  complete: "border-ink bg-ink text-paper-raised",
  current: "border-ink bg-paper-raised text-ink ring-4 ring-ink/10",
  upcoming: "border-hairline bg-paper-raised text-slate",
};

/** Five-stage indicator. Each stage links to its panel; the current one carries aria-current. */
export function IntakeProgress({ steps, onSelect }: { steps: ProgressStep[]; onSelect: (step: ProgressStep, event: MouseEvent<HTMLAnchorElement>) => void }) {
  const current = steps.find((step) => step.status === "current") ?? steps[0];
  const currentIndex = current ? steps.indexOf(current) : 0;
  return (
    <nav aria-label="Configuration steps" className="rounded-[10px] border border-hairline bg-paper-raised px-4 py-4 sm:px-6">
      <ol className="flex items-center">
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;
          return (
            <li key={step.id} className={`flex min-w-0 items-center ${isLast ? "flex-none" : "flex-1"}`}>
              <a
                href={`#${step.anchor}`}
                aria-current={step.status === "current" ? "step" : undefined}
                onClick={(event) => onSelect(step, event)}
                className="group flex min-w-0 shrink-0 items-center gap-3 rounded-md outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink"
              >
                <span className={`inline-flex size-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors ${MARKER[step.status]}`}>
                  {step.status === "complete" ? <CheckIcon /> : index + 1}
                </span>
                <span className={`hidden truncate text-sm leading-tight sm:block ${step.status === "upcoming" ? "text-slate group-hover:text-ink" : "font-medium text-ink"}`}>
                  <span className="sr-only">{step.status === "complete" ? "Completed: " : step.status === "current" ? "Current step: " : ""}</span>
                  {step.label}
                </span>
                <span className="sr-only sm:hidden">{step.label}</span>
              </a>
              {!isLast ? <span aria-hidden="true" className={`mx-2 h-px min-w-3 flex-1 sm:mx-4 ${step.status === "complete" ? "bg-ink" : "bg-hairline"}`} /> : null}
            </li>
          );
        })}
      </ol>
      <p className="mt-3 text-center text-xs font-medium text-ink sm:hidden" aria-hidden="true">
        Step {currentIndex + 1} of {steps.length} · {current?.label}
      </p>
    </nav>
  );
}
